import React, { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import Select2Dropdown from '../common/Select2Dropdown';
import { getAuthHeaders } from '../../auth';

const API_BASE = '/api/documentation/jobs';
const TOAST_CONTAINER_ID = 'InvoicePreviewToasts';

const notifyError = (message) => toast.error(message, { containerId: TOAST_CONTAINER_ID });
const notifySuccess = (message) => toast.success(message, { containerId: TOAST_CONTAINER_ID });
const notifyInfo = (message, options = {}) => toast.info(message, { containerId: TOAST_CONTAINER_ID, ...options });

const toDisplayAmount = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// PDF preview is now opened manually via the "Print PDF" button.

const InvoicePreviewDialog = ({ jobId, isPending, canCreate, canDelete, shipmentType, onClose, onGenerated, additionalInvoice = false, preSelectedJob = null }) => {
  const isRoad = String(shipmentType || '').toUpperCase().endsWith('ROAD');
  const [extraCharges, setExtraCharges] = useState([]);
  const [availableCharges, setAvailableCharges] = useState([]);
  const [existingNames, setExistingNames] = useState([]);
  const [selectedCharge, setSelectedCharge] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [savedPdfUrl, setSavedPdfUrl] = useState(null);
  const [savedInvoiceNo, setSavedInvoiceNo] = useState('');
  const [units, setUnits] = useState(1);
  const [idfNo, setIdfNo] = useState('');
  const [jobResults, setJobResults] = useState([]);
  const [selectedJob, setSelectedJob] = useState(() => {
    if (!preSelectedJob) return null;
    return { ...preSelectedJob, id: preSelectedJob.job_id || preSelectedJob.id };
  });
  const [previewData, setPreviewData] = useState(null);
  const [searching, setSearching] = useState(false);
  const previewUrlRef = useRef(null);
  const didMountRef = useRef(false);
  const extraChargesRef = useRef(extraCharges);
  useEffect(() => { extraChargesRef.current = extraCharges; }, [extraCharges]);

  const effectiveJobId = additionalInvoice ? (selectedJob ? selectedJob.id : null) : jobId;

  const fetchAvailableCharges = useCallback(async () => {
    if (!effectiveJobId) { setAvailableCharges([]); return; }
    try {
      const url = additionalInvoice
        ? `${API_BASE}/additional-invoice-charges/?job_id=${effectiveJobId}`
        : `${API_BASE}/available-invoice-charges/?job_id=${effectiveJobId}`;
      const res = await fetch(url, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch charges');
      const data = await res.json();
      const charges = (Array.isArray(data.charges) ? data.charges : []).map(c => ({ id: String(c.id), text: c.charge_name, ...c }));
      setAvailableCharges(charges);
      setExistingNames(Array.isArray(data.existing_charge_names) ? data.existing_charge_names : []);
    } catch (e) {
      notifyError(e.message || 'Failed to load available charges');
      setAvailableCharges([]);
      setExistingNames([]);
    }
  }, [effectiveJobId, additionalInvoice]);

  const refreshPreview = useCallback(async (charges, unitsValue) => {
    if (!effectiveJobId) return;
    setLoading(true);
    setPreviewData(null);
    const params = new URLSearchParams();
    params.append('job_id', effectiveJobId);
    if (charges && charges.length) {
      const payload = charges.map(c => {
        const name = c.remarks ? `${c.charge_name} (${c.remarks})` : c.charge_name;
        return { charge_name: name, unit_price: c.unit_price, units: c.units, amount: c.amount };
      });
      params.append('extra_charges', JSON.stringify(payload));
    }
    if (isRoad) {
      params.append('units', String(unitsValue || 1));
    }
    try {
      const res = await fetch(`${API_BASE}/${additionalInvoice ? 'additional-invoice-preview' : 'invoice-preview'}/?${params.toString()}`, { headers: getAuthHeaders(false), cache: 'no-store' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || data.error || 'Failed to load invoice preview.');
      }
      const blob = await res.blob();
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      const blobUrl = URL.createObjectURL(blob);
      previewUrlRef.current = blobUrl;
      setPreviewUrl(blobUrl);
      setPreviewData(null);
      return blobUrl;
    } catch (e) {
      notifyError(e.message || 'Could not open invoice preview.');
    } finally {
      setLoading(false);
    }
  }, [effectiveJobId, additionalInvoice]);

  useEffect(() => {
    setExtraCharges([]);
    setSelectedCharge('');
    fetchAvailableCharges();
    refreshPreview([], units);
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, [fetchAvailableCharges, refreshPreview]);

  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return; }
    refreshPreview(extraChargesRef.current, units);
  }, [units, refreshPreview]);

  const handleUnitsChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    const n = raw === '' ? '' : parseInt(raw, 10);
    setUnits(n === '' ? '' : Math.max(1, n));
  };

  const handleUnitsBlur = () => {
    if (units === '' || units < 1) setUnits(1);
  };

  const handleIdfSearch = useCallback(async () => {
    if (!idfNo.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(`${API_BASE}/invoicing-generated/?customer_id=ALL&search=${encodeURIComponent(idfNo)}`, { headers: getAuthHeaders(false) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to search invoices.');
      setJobResults(data.rows || []);
    } catch (e) {
      toast.error(e.message || 'Search failed.');
      setJobResults([]);
    } finally {
      setSearching(false);
    }
  }, [idfNo]);

  useEffect(() => {
    if (idfNo.trim().length < 2) { setJobResults([]); return; }
    const timer = setTimeout(handleIdfSearch, 300);
    return () => clearTimeout(timer);
  }, [idfNo]);

  const handleAddCharge = () => {
    if (!selectedCharge) return;
    const charge = availableCharges.find(c => c.id === selectedCharge);
    if (!charge) return;
    let newCharge;
    if (additionalInvoice) {
      newCharge = { charge_name: charge.charge_name.toUpperCase(), unit_price: 0, displayUnitPrice: '', units: 1, amount: 0, isAdditional: true, remarks: '' };
    } else {
      const unitPrice = Number(charge.unit_price) || 0;
      const addedUnits = isRoad ? (Number(units) || 1) : 1;
      const amount = unitPrice * addedUnits;
      newCharge = { charge_name: charge.charge_name.toUpperCase(), unit_price: unitPrice, units: addedUnits, amount, isAdditional: false, remarks: '' };
    }
    const newCharges = [...extraCharges, newCharge];
    setExtraCharges(newCharges);
    refreshPreview(newCharges, units);
    setSelectedCharge('');
  };

  const handleRemoveCharge = (index) => {
    const newCharges = extraCharges.filter((_, i) => i !== index);
    setExtraCharges(newCharges);
    refreshPreview(newCharges, units);
  };

  const handleAddAdjustment = () => {
    setExtraCharges(prev => [...prev, { charge_name: 'ADJUSTMENT CHARGE', unit_price: 0, units: 1, amount: 0, isAdjustment: true, remarks: '', displayAmount: '0', positive: false }]);
  };

  const handleAdjustmentAmountChange = (index, value) => {
    const raw = value.replace(/[^0-9.]/g, '');
    const display = raw === '' ? '' : raw;
    setExtraCharges(prev => prev.map((c, i) => i === index ? { ...c, displayAmount: display } : c));
  };

  const handleAdjustmentSignChange = (index, positive) => {
    setExtraCharges(prev => prev.map((c, i) => i === index ? { ...c, positive } : c));
  };

  const handleAdjustmentRemarksChange = (index, value) => {
    const upper = value.toUpperCase();
    setExtraCharges(prev => prev.map((c, i) => i === index ? { ...c, remarks: upper } : c));
  };

  const handleSaveAdjustment = (index) => {
    const newCharges = extraCharges.map((c, i) => {
      if (i !== index) return c;
      const raw = (c.displayAmount || '').replace(/[^0-9.]/g, '');
      let numeric = raw === '' ? 0 : Number(raw);
      if (Number.isNaN(numeric)) numeric = 0;
      const signed = c.positive ? numeric : -numeric;
      const remarks = (c.remarks || '').toUpperCase();
      return {
        ...c,
        displayAmount: String(numeric),
        unit_price: signed,
        amount: signed * c.units,
        remarks,
        charge_name: remarks ? `ADJUSTMENT CHARGE (${remarks})` : 'ADJUSTMENT CHARGE',
      };
    });
    setExtraCharges(newCharges);
    refreshPreview(newCharges, units);
  };

  const handleUnitPriceChange = (index, value) => {
    const raw = value.replace(/[^0-9.]/g, '');
    const numeric = raw === '' ? 0 : Number(raw);
    const newCharges = extraCharges.map((c, i) => {
      if (i !== index) return c;
      const up = Number.isNaN(numeric) ? 0 : numeric;
      return { ...c, unit_price: up, amount: up * (c.units || 1) };
    });
    setExtraCharges(newCharges);
    refreshPreview(newCharges, units);
  };

  const handleAdditionalUnitPriceChange = (index, value) => {
    setExtraCharges(prev => prev.map((c, i) => i === index ? { ...c, displayUnitPrice: value.replace(/[^0-9.]/g, '') } : c));
  };

  const handleSaveAdditional = (index) => {
    const newCharges = extraCharges.map((c, i) => {
      if (i !== index) return c;
      const raw = (c.displayUnitPrice || '').replace(/[^0-9.]/g, '');
      let numeric = raw === '' ? 0 : Number(raw);
      if (Number.isNaN(numeric)) numeric = 0;
      const amount = numeric * (c.units || 1);
      const remarks = (c.remarks || '').toUpperCase();
      return { ...c, unit_price: numeric, displayUnitPrice: undefined, amount, remarks };
    });
    setExtraCharges(newCharges);
    refreshPreview(newCharges, units);
  };

  const handleAdditionalRemarksChange = (index, value) => {
    const upper = value.toUpperCase();
    setExtraCharges(prev => prev.map((c, i) => i === index ? { ...c, remarks: upper } : c));
  };

  const selectableCharges = (() => {
    const seen = new Set();
    return availableCharges.filter(c => {
      const upper = (c.charge_name || '').toUpperCase();
      if (existingNames.includes(c.charge_name) || existingNames.includes(upper) || extraCharges.some(ec => (ec.charge_name || '').toUpperCase() === upper)) return false;
      if (seen.has(upper)) return false;
      seen.add(upper);
      return true;
    });
  })();

  const handlePrintPdf = () => {
    if (!savedPdfUrl) return;
    const pdfUrl = savedPdfUrl.startsWith('http') ? savedPdfUrl : window.location.origin + savedPdfUrl;
    const cleanUrl = savedPdfUrl.split('?')[0];
    let filename = cleanUrl.split('/').pop() || 'invoice.pdf';
    if (savedInvoiceNo) {
      filename = `Invoice_${savedInvoiceNo}.pdf`;
    }
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = filename;
    a.target = '_blank';
    a.rel = 'noopener,noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    if (onClose) onClose();
  };

  const handleGenerate = async () => {
    if (!canCreate) { toast.error('You do not have permission to generate invoices.'); return; }
    if (!effectiveJobId) { toast.error('Select a job first.'); return; }
    if (additionalInvoice) {
      const unsavedPositive = extraCharges.some(c => c.isAdditional && (c.displayUnitPrice !== undefined && Number(c.displayUnitPrice) > 0));
      if (unsavedPositive) { notifyError('Please save the charge before generating.'); return; }
      const total = extraCharges.reduce((s, c) => s + (Number(c.amount) || 0), 0);
      if (total <= 0) { notifyError('Add at least one charge with a value greater than 0 before generating.'); return; }
    }
    setGenerating(true);
    try {
      const url = additionalInvoice ? 'additional-invoice-generate' : 'invoicing-generate';
      const payload = additionalInvoice
        ? { job_id: effectiveJobId, extra_charges: extraCharges.map(c => ({ charge_name: c.remarks ? `${c.charge_name} (${c.remarks})` : c.charge_name, unit_price: c.unit_price, units: c.units, amount: c.amount })) }
        : { job_id: effectiveJobId, extra_charges: extraCharges.map(c => ({ charge_name: c.charge_name, unit_price: c.unit_price, units: c.units, amount: c.amount })), units: isRoad ? (units || 1) : 1 };
      const res = await fetch(`${API_BASE}/${url}/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to generate invoice.');
      notifySuccess(additionalInvoice ? 'Additional invoice generated successfully.' : 'Invoice generated successfully.');
      if (onGenerated) onGenerated(effectiveJobId);
      
      if (data.pdf_url) { setSavedPdfUrl(data.pdf_url); setSavedInvoiceNo(data.invoice ? data.invoice.invoice_no : (data.additional_invoice ? data.additional_invoice.invoice_no : '')); }
    } catch (e) {
      notifyError(e.message || 'Failed to generate invoice.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', padding: 12, boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>{additionalInvoice ? 'Additional Invoice' : 'Invoice Preview'}</h3>
        <button className="add-btn" style={{ background: '#6c757d', fontSize: 11, padding: '3px 10px', minHeight: 26 }} onClick={onClose}>Close</button>
      </div>

      {additionalInvoice && !preSelectedJob && !selectedJob && (
        <div style={{ marginBottom: 8, position: 'relative', width: 260 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <label style={{ fontSize: 11, color: '#444', fontWeight: 600 }}>IDF NUMBER</label>
            <input
              placeholder="Start typing IDF..."
              value={idfNo}
              onChange={e => setIdfNo(e.target.value)}
              style={{ width: '100%', height: 24, padding: '1px 6px', fontSize: 12, border: '1px solid #ccc', borderRadius: 3, boxSizing: 'border-box' }}
            />
          </div>
          {searching && <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>Searching...</div>}
          {jobResults.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: 120, overflowY: 'auto', border: '1px solid #ccc', borderRadius: 3, background: '#fff', zIndex: 100, marginTop: 2 }}>
              {jobResults.map(j => (
                <div key={j.id} style={{ padding: '6px 10px', cursor: 'pointer', borderBottom: '1px solid #eee', fontSize: 12 }} onClick={() => setSelectedJob(j)} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f5f5f5'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
                  <strong>{j.idf_no || '-'}</strong> &mdash; {j.invoice_no || '-'} &mdash; {j.customer_name || '-'}
                </div>
              ))}
            </div>
          )}
          {jobResults.length === 0 && idfNo.trim().length >= 2 && !searching && (
            <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>No generated invoices found.</div>
          )}
        </div>
      )}

      {additionalInvoice && selectedJob && (
        <div style={{ marginBottom: 8, fontSize: 12, color: '#444' }}>
          <strong>Selected:</strong> {selectedJob.idf_no || '-'} | <strong>Invoice:</strong> {selectedJob.invoice_no || '-'}
        </div>
      )}

      {(isPending || (additionalInvoice && selectedJob)) && (
        <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: 8 }}>
          <div style={{ flex: '1 1 0', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width: 240, flex: '0 0 auto' }}>
              <label style={{ fontSize: 11, color: '#444', fontWeight: 600 }}>ADD CHARGE</label>
              <Select2Dropdown
                options={selectableCharges}
                value={selectedCharge}
                onChange={setSelectedCharge}
                placeholder="Select a charge..."
                displayKey="text"
                valueKey="id"
                clearable
              />
            </div>
            <button className="add-btn" style={{ background: '#007bff', fontSize: 11, padding: '3px 10px', minHeight: 26 }} onClick={handleAddCharge} disabled={!selectedCharge}>Add Charge</button>
            {!additionalInvoice && canDelete && (
              <button className="add-btn" style={{ background: '#6f42c1', fontSize: 11, padding: '3px 10px', minHeight: 26 }} onClick={handleAddAdjustment}>ADJUSTMENT</button>
            )}
          </div>
          {isRoad && !additionalInvoice && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: '0 0 auto', alignItems: 'center' }}>
              <label style={{ fontSize: 11, color: '#444', fontWeight: 600 }}>NO. OF TRUCKS</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={units}
                onChange={handleUnitsChange}
                onBlur={handleUnitsBlur}
                style={{
                  boxSizing: 'border-box',
                  width: 28,
                  height: 24,
                  padding: 0,
                  fontSize: 12,
                  textAlign: 'center',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                }}
              />
            </div>
          )}
          {canCreate && (
            <button className="add-btn" style={{ background: '#28a745', fontSize: 11, padding: '3px 10px', minHeight: 26, marginLeft: 8 }} onClick={handleGenerate} disabled={generating}>
              {generating ? 'Generating...' : (additionalInvoice ? 'Generate Additional Invoice' : 'Generate Invoice')}
            </button>
          )}
          {savedPdfUrl && (
            <button className="add-btn" style={{ background: '#17a2b8', fontSize: 11, padding: '3px 10px', minHeight: 26, marginLeft: 8 }} onClick={handlePrintPdf}>
              Print PDF
            </button>
          )}
        </div>
      )}
      {extraCharges.length > 0 && (isPending || additionalInvoice) && (
        <table className="data-table" style={{ width: '100%', marginBottom: 8, borderCollapse: 'collapse', border: '1px solid #ddd', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #ddd', padding: '4px', width: 180, textAlign: 'center' }}>CHARGE NAME</th>
              <th style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'center' }}>UNITS</th>
              <th style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'center' }}>UNIT CHARGE</th>
              <th style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'center' }}>TOTAL</th>
              <th style={{ border: '1px solid #ddd', padding: '4px', width: 100, textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {extraCharges.map((c, i) => (
              <tr key={i}>
                <td style={{ border: '1px solid #ddd', padding: '4px', width: 180 }}>
                  {c.isAdjustment ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <strong style={{ fontSize: 12 }}>{c.charge_name}</strong>
                      <input
                        type="text"
                        value={c.remarks || ''}
                        onChange={(e) => handleAdjustmentRemarksChange(i, e.target.value)}
                        placeholder="Remarks"
                        style={{ fontSize: 12, padding: '2px 4px', border: '1px solid #ccc', borderRadius: 3 }}
                      />
                    </div>
                  ) : c.isAdditional ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <strong style={{ fontSize: 12 }}>{c.charge_name}</strong>
                      <input
                        type="text"
                        value={c.remarks || ''}
                        onChange={(e) => handleAdditionalRemarksChange(i, e.target.value)}
                        placeholder="Remarks"
                        style={{ fontSize: 12, padding: '2px 4px', border: '1px solid #ccc', borderRadius: 3 }}
                      />
                    </div>
                  ) : c.charge_name}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'center' }}>{c.units}</td>
                <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>
                  {c.isAdjustment ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleAdjustmentSignChange(i, false)}
                        style={{ padding: '2px 6px', fontSize: 12, border: '1px solid #ccc', borderRadius: 3, background: !c.positive ? '#dc3545' : '#fff', color: !c.positive ? '#fff' : '#333', cursor: 'pointer' }}
                      >-</button>
                      <button
                        onClick={() => handleAdjustmentSignChange(i, true)}
                        style={{ padding: '2px 6px', fontSize: 12, border: '1px solid #ccc', borderRadius: 3, background: c.positive ? '#28a745' : '#fff', color: c.positive ? '#fff' : '#333', cursor: 'pointer' }}
                      >+</button>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={c.displayAmount}
                        onChange={(e) => handleAdjustmentAmountChange(i, e.target.value)}
                        style={{ width: 80, textAlign: 'right', fontSize: 12, padding: '2px 4px', border: '1px solid #ccc', borderRadius: 3 }}
                      />
                    </div>
                  ) : c.isAdditional ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={c.displayUnitPrice !== undefined ? c.displayUnitPrice : (c.unit_price || '')}
                        onChange={(e) => handleAdditionalUnitPriceChange(i, e.target.value)}
                        style={{ width: 80, textAlign: 'right', fontSize: 12, padding: '2px 4px', border: '1px solid #ccc', borderRadius: 3 }}
                      />
                    </div>
                  ) : toDisplayAmount(c.unit_price)}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>{toDisplayAmount(c.amount)}</td>
                <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'center' }}>
                  {c.isAdjustment && (
                    <button className="add-btn" style={{ background: '#28a745', fontSize: 11, padding: '2px 8px', minHeight: 22, marginRight: 4 }} onClick={() => handleSaveAdjustment(i)}>Save</button>
                  )}
                  {c.isAdditional && (
                    <button className="add-btn" style={{ background: '#28a745', fontSize: 11, padding: '2px 8px', minHeight: 22, marginRight: 4 }} onClick={() => handleSaveAdditional(i)}>Save</button>
                  )}
                  <button className="add-btn" style={{ background: '#dc3545', fontSize: 11, padding: '2px 8px', minHeight: 22 }} onClick={() => handleRemoveCharge(i)}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ flex: 1, border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden', background: '#fff', position: 'relative' }}>
        {loading && <div className="loading-indicator" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>Loading preview...</div>}
        {previewUrl && (
          <iframe src={previewUrl} title="Invoice Preview" style={{ width: '100%', height: '100%', border: 'none' }} />
        )}
      </div>
    </div>
  );
};

export default InvoicePreviewDialog;
